import axios from 'axios';
import Hashids from 'hashids';
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button, Card } from 'reactstrap';
import { v4 as uuidv4 } from 'uuid';

import {
  getApiErrorMessage,
  hasAdminRole,
  hasAnyPermission,
} from '@/api/admin-access';
import AiCredits from '@/components/ai-credits';
import Breadcrumb from '@/components/common/breadcrumb.component';
import MediaCropper from '@/components/cropper';
import { Constants } from '@/constants';
import { UserStore } from '@/stores';
import { uploadToS3 } from '@/utils/imageUtils';

const hashids = new Hashids('', 12);
const emptyBrand = {
  name: '',
  description: '',
  website: '',
  blog_url: '',
  podcast_url: '',
  twitter_id: '',
  facebook_id: '',
  instagram_id: '',
  cigar_brand: true,
  active: true,
  logo_image: '',
  cover_image: '',
};

const BrandEditor = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const access = UserStore.useState((state) => state.access);
  const canWrite = hasAnyPermission(access, ['cigars', 'businesses'], 'write');
  const isNew = id === 'new';
  const brandId = isNew ? null : Number(hashids.decode(id || '')[0]) || null;
  const [brand, setBrand] = useState(emptyBrand);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [crop, setCrop] = useState<{
    field: 'logo_image' | 'cover_image';
    src: string;
  } | null>(null);

  useEffect(() => {
    let active = true;
    setBrand(emptyBrand);
    setError('');
    if (isNew) {
      setLoading(false);
      return;
    }
    if (!brandId) {
      setError('This brand link is invalid.');
      setLoading(false);
      return;
    }
    setLoading(true);
    axios
      .get(`${Constants.apiPath}/brands/datatables`, {
        params: { id: brandId },
      })
      .then(({ data }) => {
        const found = data.data?.find((entry) => Number(entry.id) === brandId);
        if (!found) throw new Error('Brand not found.');
        if (active)
          setBrand(
            Object.fromEntries(
              Object.entries(emptyBrand).map(([key, fallback]) => {
                if (key === 'logo_image' || key === 'cover_image') {
                  return [key, found[`${key}_url`] || found[key] || ''];
                }
                return [key, found[key] ?? fallback];
              })
            ) as typeof emptyBrand
          );
      })
      .catch((err) => {
        if (active) setError(getApiErrorMessage(err));
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [brandId, isNew]);

  const save = async () => {
    if (!canWrite || saving || uploading || !brand.name.trim()) return;
    setSaving(true);
    try {
      const payload = {
        ...brand,
        name: brand.name.trim(),
        logo_image: brand.logo_image || undefined,
        cover_image: brand.cover_image || undefined,
        twitter_id: brand.twitter_id.replace(/^@/, ''),
        instagram_id: brand.instagram_id.replace(/^@/, ''),
      };
      const result = isNew
        ? await axios.post(`${Constants.apiPath}/brands`, payload)
        : await axios.put(`${Constants.apiPath}/brands/${brandId}`, payload);
      toast.success(isNew ? 'Brand created.' : 'Brand saved.');
      if (isNew)
        navigate(
          result.data?.id
            ? `/brands/editor/${hashids.encode(result.data.id)}`
            : '/brands/manager',
          { replace: true }
        );
    } catch (err) {
      toast.error(getApiErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };
  const selectImage = (field: 'logo_image' | 'cover_image', file?: File) => {
    if (!file) return;
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      toast.error('Choose a JPG, PNG, or WebP image.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => setCrop({ field, src: String(reader.result) });
    reader.onerror = () => toast.error('Could not read this image.');
    reader.readAsDataURL(file);
  };
  const upload = (canvas: HTMLCanvasElement) => {
    if (!crop) return;
    const { field } = crop;
    setCrop(null);
    setUploading(true);
    canvas.toBlob(async (blob) => {
      try {
        if (!blob) throw new Error('Could not prepare the image.');
        const uuid = uuidv4();
        const result = await uploadToS3(
          blob,
          `brands/${field === 'logo_image' ? 'brandLogo' : 'brandCoverImage'}`,
          `${uuid}.png`,
          uuid
        );
        setBrand((current) => ({ ...current, [field]: result.data.media_url }));
      } catch (err) {
        toast.error(getApiErrorMessage(err));
      } finally {
        setUploading(false);
      }
    }, 'image/png');
  };
  const field = (key: keyof typeof emptyBrand, label: string) => (
    <div className="mb-3" key={key}>
      <label htmlFor={`brand-${key}`}>{label}</label>
      <input
        id={`brand-${key}`}
        className="form-control"
        value={String(brand[key])}
        disabled={!canWrite || saving}
        onChange={(event) => setBrand({ ...brand, [key]: event.target.value })}
      />
    </div>
  );

  const saveLabel = isNew ? 'Create Brand' : 'Save Changes';
  return (
    <>
      <Breadcrumb
        title={isNew ? 'Add New Brand' : brand.name || 'Brand Editor'}
        label="Editor"
        parent="Brands"
      />
      <div className="container-fluid" style={{ maxWidth: 1200 }}>
        {loading && <p role="status">Loading brand…</p>}
        {!loading && error && (
          <div role="alert" className="alert alert-danger">
            {error}
          </div>
        )}
        {!loading && !error && (
          <div className="row">
            <div className={hasAdminRole(access) ? 'col-md-8' : 'col-12'}>
              <Card style={{ padding: 20, marginTop: 12 }}>
                <h5>Brand images</h5>
                <div className="row">
                  {(['logo_image', 'cover_image'] as const).map((key) => (
                    <div
                      className={key === 'logo_image' ? 'col-md-4' : 'col-md-8'}
                      key={key}
                    >
                      <label htmlFor={`brand-${key}`}>
                        {key === 'logo_image' ? 'Logo' : 'Cover image'}
                      </label>
                      {brand[key] && (
                        <img
                          src={brand[key]}
                          alt={
                            key === 'logo_image' ? 'Brand logo' : 'Brand cover'
                          }
                          style={{
                            width: '100%',
                            height: 150,
                            objectFit: 'contain',
                            marginBottom: 10,
                          }}
                        />
                      )}
                      <input
                        id={`brand-${key}`}
                        className="form-control"
                        type="file"
                        accept="image/jpeg,image/png,image/webp"
                        disabled={!canWrite || saving || uploading}
                        onChange={(event) => {
                          selectImage(key, event.target.files?.[0]);
                          event.target.value = '';
                        }}
                      />
                    </div>
                  ))}
                </div>
                {uploading && <p role="status">Uploading image…</p>}
              </Card>
              <Card style={{ padding: 20, marginTop: 12 }}>
                <h5>Brand details</h5>
                {field('name', 'Name')}
                <label htmlFor="brand-description">Description</label>
                <textarea
                  id="brand-description"
                  className="form-control mb-3"
                  value={brand.description}
                  disabled={!canWrite || saving}
                  onChange={(event) =>
                    setBrand({ ...brand, description: event.target.value })
                  }
                />
                <label>
                  <input
                    type="checkbox"
                    checked={brand.cigar_brand}
                    disabled={!canWrite || saving}
                    onChange={(event) =>
                      setBrand({ ...brand, cigar_brand: event.target.checked })
                    }
                  />{' '}
                  Cigar brand
                </label>
              </Card>
              <Card style={{ padding: 20, marginTop: 12 }}>
                <h5>Website and feeds</h5>
                {field('website', 'Website')}
                {field('blog_url', 'Blog RSS Feed')}
                {field('podcast_url', 'Podcast RSS Feed')}
              </Card>
              <Card style={{ padding: 20, marginTop: 12 }}>
                <h5>Social</h5>
                {field('twitter_id', 'X (Twitter)')}
                {field('facebook_id', 'Facebook')}
                {field('instagram_id', 'Instagram')}
              </Card>
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'flex-end',
                  gap: 10,
                  padding: '20px 0 36px',
                }}
              >
                <Button
                  outline
                  disabled={saving || uploading}
                  onClick={() => navigate('/brands/manager')}
                >
                  Cancel
                </Button>
                <Button
                  color="primary"
                  disabled={
                    !canWrite || saving || uploading || !brand.name.trim()
                  }
                  onClick={save}
                >
                  {saving ? 'Saving…' : saveLabel}
                </Button>
              </div>
            </div>
            {hasAdminRole(access) && (
              <div className="col-md-4">
                <AiCredits accountType="brand" accountId={brandId} />
              </div>
            )}
          </div>
        )}
      </div>
      <MediaCropper
        circle={crop?.field === 'logo_image'}
        aspectRatio={crop?.field === 'logo_image' ? 1 : 1000 / 300}
        src={crop?.src}
        open={Boolean(crop)}
        toggle={() => setCrop(null)}
        onClose={() => setCrop(null)}
        onSave={upload}
      />
    </>
  );
};
export default BrandEditor;
